import {
  VStack,
  HStack,
  Text,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { useFormContext, Path, Controller } from "react-hook-form";
import { KYDInput, KYDSelect } from "src/ui";
import { CreateSegmentFormValues as FormValues } from ".";
import {
  KYDNumericFilterKey,
  KYDNumericOperator,
} from "@common/interfaces/KYDCampaign";

export interface NumericFilter {
  operator: KYDNumericOperator;
  value: number;
}

export interface FilterItemProps<K extends KYDNumericFilterKey> {
  label: string;
  keyName: K;
  hasRightBorder?: boolean;
  valuePostfix?: string;
  minValue?: number;
  maxValue?: number;
}

const FilterItem = <K extends KYDNumericFilterKey>({
  label,
  keyName,
  hasRightBorder = true,
  valuePostfix,
  minValue,
  maxValue,
}: FilterItemProps<K>) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<FormValues>();

  const operatorPath = `filters.${keyName}.operator` as Path<FormValues>;
  const valuePath = `filters.${keyName}.value` as Path<FormValues>;

  return (
    <VStack
      flex="1"
      align="flex-start"
      gap={2}
      borderRight={hasRightBorder ? "1px solid rgba(0, 0, 0, 0.20)" : "none"}
      pr={hasRightBorder ? 4 : 0}
      mr={hasRightBorder ? 4 : 0}
    >
      <Text
        overflow="hidden"
        color="rgba(26, 32, 44, 0.75)"
        textOverflow="ellipsis"
        fontSize="14px"
        fontWeight="500"
      >
        {label}
      </Text>
      <HStack w="100%">
        <KYDSelect
          flex="1"
          options={[
            { label: "Greater than", value: ">" },
            { label: "Greater or equal to", value: ">=" },
            { label: "Less than", value: "<" },
            { label: "Less or equal to", value: "<=" },
            { label: "Equal", value: "=" },
          ]}
          {...register(operatorPath, { required: "Operator is required" })}
          defaultValue="GREATER_THAN"
        />

        <InputGroup width="70px">
          <Controller
            control={control}
            name={valuePath}
            defaultValue={0}
            render={({ field }) => (
              <KYDInput
                {...field}
                value={field.value as number}
                width={valuePostfix ? "30px" : "65px"}
                type="number"
                containerStyle={{ py: 2 }}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    return field.onChange("");
                  }
                  const num = parseFloat(raw);
                  if (minValue !== undefined && num < minValue) {
                    return field.onChange(minValue);
                  }
                  if (maxValue !== undefined && num > maxValue) {
                    return field.onChange(maxValue);
                  }
                  field.onChange(num);
                }}
              />
            )}
          />
          {valuePostfix && (
            <InputRightElement pointerEvents="none">
              {valuePostfix}
            </InputRightElement>
          )}
        </InputGroup>
      </HStack>
    </VStack>
  );
};

export default FilterItem;
